import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { BookOpen, Briefcase, Home, GraduationCap, DollarSign, Heart, FileText, Scale } from 'lucide-react';

const Resources = () => {
  const categories = [
    {
      icon: Briefcase,
      title: "Employment & Job Search",
      description: "Learn how to find jobs, build your resume, and succeed in interviews",
      topics: [
        "Building Your First Resume",
        "Job Search Strategies",
        "Interview Preparation",
        "Understanding Work Authorization"
      ],
      color: "emerald"
    },
    {
      icon: Home,
      title: "Housing & Renting",
      description: "Navigate the rental market and understand your housing rights",
      topics: [
        "Finding an Apartment",
        "Understanding Lease Agreements",
        "Tenant Rights and Responsibilities",
        "Setting Up Utilities"
      ],
      color: "blue"
    },
    {
      icon: GraduationCap,
      title: "Education & College",
      description: "Access education opportunities and apply for college",
      topics: [
        "Applying to College",
        "Financial Aid and Scholarships",
        "English Language Programs",
        "Credential Evaluation"
      ],
      color: "purple"
    },
    {
      icon: DollarSign,
      title: "Financial Literacy",
      description: "Build financial stability and understand the U.S. banking system",
      topics: [
        "Opening a Bank Account",
        "Building Credit",
        "Creating a Budget",
        "Understanding Taxes"
      ],
      color: "teal"
    },
    {
      icon: Heart,
      title: "Healthcare Basics",
      description: "Understand the U.S. healthcare system and access medical services",
      topics: [
        "Finding a Doctor",
        "Health Insurance Basics",
        "Emergency Services",
        "Prescription Medications"
      ],
      color: "red"
    },
    {
      icon: Scale,
      title: "Legal & Immigration",
      description: "Navigate immigration processes and understand your legal rights",
      topics: [
        "Understanding Your Status",
        "Path to Permanent Residency",
        "Working with Immigration Lawyers",
        "Know Your Rights"
      ],
      color: "indigo"
    },
    {
      icon: FileText,
      title: "Essential Documents",
      description: "Get the documents you need to establish yourself in the U.S.",
      topics: [
        "Social Security Number",
        "Driver's License",
        "State ID",
        "Important Records"
      ],
      color: "orange"
    },
    {
      icon: BookOpen,
      title: "Daily Life Skills",
      description: "Learn practical skills for everyday life in America",
      topics: [
        "Using Public Transportation",
        "Shopping and Groceries",
        "Cultural Adjustment",
        "Community Resources"
      ],
      color: "pink"
    }
  ];

  const colorClasses = {
    emerald: "from-emerald-500 to-teal-600",
    blue: "from-blue-500 to-cyan-600",
    purple: "from-purple-500 to-pink-600",
    teal: "from-teal-500 to-emerald-600",
    red: "from-red-500 to-pink-600",
    indigo: "from-indigo-500 to-purple-600",
    orange: "from-orange-500 to-red-600",
    pink: "from-pink-500 to-rose-600"
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-20 px-4">
        <div className="container mx-auto max-w-6xl text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            Resources for New Arrivals
          </h1>
          <p className="text-xl text-emerald-100 max-w-3xl mx-auto">
            Educational content to help Ukrainian families navigate life in the United States. 
            From finding employment to understanding healthcare, we're here to guide you every step of the way.
          </p>
        </div>
      </section>

      {/* Coming Soon Notice */}
      <section className="py-12 px-4 bg-blue-50 border-b border-blue-100">
        <div className="container mx-auto max-w-6xl">
          <Card className="bg-gradient-to-r from-blue-500 to-cyan-600 text-white border-none">
            <CardContent className="pt-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-2">🎬 Animated Video Guides Coming Soon!</h2>
                <p className="text-blue-100 text-lg">
                  We're creating step-by-step animated tutorials for each topic below. 
                  Check back soon for easy-to-follow video guides in English and Ukrainian!
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((category, index) => {
              const Icon = category.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="pt-6">
                    <div className={`w-16 h-16 rounded-lg bg-gradient-to-br ${colorClasses[category.color]} flex items-center justify-center mb-4`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{category.title}</h3>
                    <p className="text-slate-600 mb-4">{category.description}</p>
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-slate-700">Topics covered:</p>
                      <ul className="space-y-1">
                        {category.topics.map((topic, topicIndex) => (
                          <li key={topicIndex} className="text-sm text-slate-600 flex items-start">
                            <span className="text-emerald-600 mr-2">•</span>
                            {topic}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <span className="text-sm text-slate-500 italic">Video guide coming soon</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-emerald-50 to-teal-50">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Need Help Now?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            While we're creating these resources, we're here to support you. 
            Reach out if you need guidance or have questions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/#contact"
              className="inline-block px-8 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold rounded-lg hover:from-emerald-700 hover:to-teal-700 transition-all shadow-lg"
            >
              Contact Us
            </a>
            <a
              href="/book-speaking"
              className="inline-block px-8 py-4 bg-white text-emerald-600 font-semibold rounded-lg hover:bg-emerald-50 transition-all shadow-lg border-2 border-emerald-600"
            >
              Book a Speaking Event
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resources;
